import type { DataGlueGraph } from '@glueapp/graph-types';
import type { ExecutionResult } from '../useGraphExecution';

export interface RunCompatibility {
    isFullyCompatible: boolean;
    compatibleNodeIds: Set<number>;
    missingNodeIds: Set<number>;
    message: string | null;
}

export function checkRunCompatibility(graph: DataGlueGraph, executionResult: ExecutionResult): RunCompatibility {
    const compatibleNodeIds = new Set<number>();
    const missingNodeIds = new Set<number>();
    const nodeMap = new Map<number, (typeof graph.nodeInstances)[number]>(graph.nodeInstances.map(n => [n.id, n]));

    for (const nodeIdStr of Object.keys(executionResult)) {
        const nodeId = Number(nodeIdStr);
        const node = nodeMap.get(nodeId);

        if (!node) {
            missingNodeIds.add(nodeId);
            continue;
        }

        const result = executionResult[nodeId];
        const outPortNames = new Set(node.outPorts.map((p: { name: string }) => p.name));
        const resultPortNames = Object.keys(result.output);
        const allPortsExist = resultPortNames.every(name => outPortNames.has(name));

        if (allPortsExist) {
            compatibleNodeIds.add(nodeId);
        } else {
            missingNodeIds.add(nodeId);
        }
    }

    const isFullyCompatible = missingNodeIds.size === 0;
    let message: string | null = null;

    if (!isFullyCompatible) {
        const count = missingNodeIds.size;
        message = `${count} node${count === 1 ? ' has' : 's have'} changed since this run. Some results may not be shown.`;
    }

    return { isFullyCompatible, compatibleNodeIds, missingNodeIds, message };
}

export function filterToCompatibleResult(executionResult: ExecutionResult, compatibility: RunCompatibility): ExecutionResult {
    const filtered: ExecutionResult = {};
    for (const nodeId of compatibility.compatibleNodeIds) {
        if (executionResult[nodeId]) {
            filtered[nodeId] = executionResult[nodeId];
        }
    }
    return filtered;
}
